import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { contrastChart, Spinner } from '@iq/react-components';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Legend, Cell } from 'recharts';

import DegradedQualityIndicator from '../shared/DegradedQualityIndicator';
import classnames from 'classnames';
import Loader from '../../../../Loader';
import { getSiteVariables } from '../../../../../bundles/sources';
import { format } from '../../../../../units';
import { useClientSize } from '../../../../../utils';
import { DropdownMenu } from '../../../../DropdownMenu';

const getColor = (index, config) => {
  if (config.color) {
    return config.color;
  }

  return contrastChart[index % contrastChart.length];
};

const PieVisualization = ({
  visualization,
  isPreview,
  isFullscreen,
  onFullscreen,
  CustomTooltip,
  getMoreOptions,
  dropdownPlacement,
  disconnected,
  latestValues: values,
  initialLoaded: initialLoadedOverride,
  loading: loadingOverride,
}) => {
  const initialLoaded = initialLoadedOverride ?? Boolean(values);
  const loading = loadingOverride ?? !values;
  const siteVariables = useSelector(getSiteVariables);
  const [{ height }, clientReference] = useClientSize();
  const { variables = [], configuration = {}, name: visualizationName } = visualization;

  const isDataInvalid = useMemo(() => {
    if (disconnected) return true;
    if (!initialLoaded || !values) return false;
    if (variables.length > 0 && values.length === 0) return true;
    return values.some(({ value: signalValue }) => {
      if (!Array.isArray(signalValue) || signalValue.length < 3) return true;
      const [, sliceValue, sliceQuality] = signalValue;
      return sliceQuality > 1 || sliceValue == null || Number.isNaN(sliceValue);
    });
  }, [disconnected, initialLoaded, values, variables.length]);

  const CustomLegend = ({ payload }) => (
    <ul className="pie-legend">
      {payload.map((dataSet, index) => {
        const key = `${dataSet.color} + ${index}`;
        return (
          <li
            key={key}
            className="pie-legend-item"
          >
            <svg
              width="10"
              height="10"
            >
              <rect
                width="10"
                height="10"
                fill={dataSet.color}
              />
            </svg>
            {dataSet.value}
          </li>
        );
      })}
    </ul>
  );

  const data = useMemo(
    () =>
      values?.map(({ value: [, sliceValue] = [], variable }, index) => {
        const siteVariable = siteVariables?.find((sv) => sv.id === variable);
        if (!siteVariable) return null;

        // Determine the unit label to use for formatting if the user selects a unit from the dropdown other than the default.
        // If no custom unit label is added, configuration.unitLabel is undefined.
        const unitLabel =
          configuration.unit !== 'variable-default' && !configuration.unitLabel
            ? configuration.unit
            : configuration.unitLabel;

        const displayUnit =
          configuration.unit === 'variable-default' ? siteVariable?.unit || '' : configuration.unit;
        const { value: formattedValue, unit } = format(
          displayUnit,
          sliceValue,
          configuration.decimals,
          unitLabel
        );

        return {
          value: Number.isNaN(formattedValue) ? null : Number.parseFloat(formattedValue),
          fill: getColor(index, {}),
          name: variables[index]?.label || siteVariable?.name,
          decimals: configuration.decimals,
          unit,
        };
      }),
    [
      values,
      configuration.decimals,
      configuration.unit,
      configuration.unitLabel,
      siteVariables,
      variables,
    ]
  );

  const vizHeight = height - 28; // less header
  const loaderHeight = vizHeight - 28; // less loader padding

  const classes = classnames('menu', {});

  const hasValidData = data?.some(
    (item) => item?.value !== null && item?.value !== undefined && !Number.isNaN(item?.value)
  );
  const isVizEmpty = !hasValidData && !loading && initialLoaded;
  const showOptionsMenu = !isPreview && !isVizEmpty;

  return (
    <div
      className={`pie-visualization-component ${isDataInvalid ? 'invalid-data' : ''}`}
      ref={clientReference}
    >
      {!isPreview && !isFullscreen ? (
        <div
          className="pie-header"
          onDoubleClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className="pie-title truncateText">
            <CustomTooltip content={configuration?.title || visualizationName}>
              <span className="title">{configuration?.title || visualizationName || '\u00A0'}</span>
            </CustomTooltip>
            {isDataInvalid ? <DegradedQualityIndicator /> : null}
          </div>
          <div className={classes}>
            {initialLoaded && loading && (
              <Spinner
                size="s"
                className="spinner"
              />
            )}
            {showOptionsMenu && (
              <DropdownMenu
                trigger="he-moreoptionsvertical"
                triggerSize="s"
                menuOptions={getMoreOptions({ onFullscreen })}
                menuXPlacement={dropdownPlacement?.x}
                menuYPlacement={dropdownPlacement?.y}
                styles={{
                  dropdownIndicator: { padding: '0px' },
                  option: { cursor: 'pointer' },
                }}
              />
            )}
          </div>
        </div>
      ) : (
        !isFullscreen && (
          <div className="pie-header">
            <div className="pie-title truncateText">
              <span className="title">{configuration.title || visualizationName || '\u00A0'}</span>
              {/* {isDataInvalid ? <DegradedQualityIndicator /> : null} */}
            </div>
            {initialLoaded && loading && (
              <Spinner
                size="s"
                className="spinner"
              />
            )}
          </div>
        )
      )}

      {!initialLoaded && loading ? (
        <Loader
          text="Loading ..."
          height={loaderHeight}
        />
      ) : (
        <div className="pie-wrapper">
          <ResponsiveContainer
            width="100%"
            height={vizHeight}
          >
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                innerRadius={configuration.pieType === 'Solid' ? '0%' : '80%'}
                paddingAngle={configuration.sliceGap}
                outerRadius="100%"
                isAnimationActive={false}
              >
                {data?.map((slice, index) => (
                  <Cell
                    key={index}
                    fill={slice.fill}
                    fillOpacity={1}
                    stroke={slice.fill}
                  />
                ))}
              </Pie>

              <Tooltip
                isAnimationActive={false}
                labelStyle={{ marginBottom: '0.4rem' }}
                formatter={(value, name, formatterData) => {
                  const numericValue = formatterData.payload.value;
                  if (numericValue == null || Number.isNaN(numericValue)) {
                    return [`— ${formatterData.payload.unit}`, formatterData.payload.name];
                  }
                  const total = data?.reduce(
                    (acc, current) => acc + (Number.isFinite(current?.value) ? current.value : 0),
                    0
                  );
                  const percentage = total ? numericValue / total : 0;
                  return [
                    `${Number.parseFloat(numericValue).toFixed(formatterData.payload.decimals)} ${formatterData.payload.unit} (${(percentage * 100).toFixed(2)}%)`,
                    formatterData.payload.name,
                  ];
                }}
                allowEscapeViewBox={{ x: false, y: true }}
                wrapperStyle={{ zIndex: '1000' }}
              />
              {configuration.legend?.show && <Legend content={<CustomLegend />} />}
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PieVisualization;
